import { el } from "../utils/dom.ts";
import {
  createFxPanel,
  createGoldIntlPanel,
  createGoldPremiumPanel,
  createGoldVnPanel,
  createOilPanel,
  createVnIndexPanel,
} from "../components/price-panels.ts";
import { AnalysisBriefPanel } from "../components/analysis-brief-panel.ts";
import { CpiChartPanel } from "../components/cpi-chart-panel.ts";
import { StatTilesPanel } from "../components/stat-tiles-panel.ts";
import { ECONOMY_TOPIC_FILTERS, NewsFeedPanel } from "../components/news-feed-panel.ts";
import { SentimentTrendPanel } from "../components/sentiment-trend-panel.ts";
import { SourceStatusBadge } from "../components/source-status-badge.ts";
import { showFirstVisitDisclaimer } from "../components/first-visit-disclaimer.ts";
import { AlertFeedPanel } from "../components/alert-feed-panel.ts";
import { EventsHeatmapPanel } from "../components/events-heatmap-panel.ts";
import { DomainRankingPanel } from "../components/domain-ranking-panel.ts";
import { DomainInsightBlock } from "../components/domain-insight-block.ts";
import { DomainPulsePanel } from "../components/domain-pulse-panel.ts";
import { PolicyActivityChart } from "../components/policy-activity-chart.ts";
import { HealthGaugePanel } from "../components/health-gauge-panel.ts";
import { SentimentWeeklyChart } from "../components/sentiment-weekly-chart.ts";
import { signalSourceLabel } from "../components/signal-source-label.ts";
import { loadProvinceNames } from "../services/province-names.ts";
import { appContext } from "./app-context.ts";
import {
  currentRoute,
  DEFAULT_TAB,
  goToTab,
  normalizeLegacyHash,
  onRouteChange,
  type Route,
  TAB_LABELS,
  TABS,
  type Tab,
} from "./tab-router.ts";

// Each tab builds its content lazily on first activation and caches the element.
type TabBuilder = () => HTMLElement;

export function initApp(root: HTMLElement): void {
  const badge = new SourceStatusBadge();

  const header = el("header", { className: "app-header" }, [
    el("div", { className: "brand" }, [
      el("span", { className: "brand-name" }, ["My Vietnam Beat"]),
      el("span", { className: "brand-tag" }, ["Nhịp kinh tế Việt Nam"]),
    ]),
    buildTabNav(),
    badge.el,
  ]);

  const tabHost = el("div", { className: "tab-host" });
  const footer = el("footer", { className: "app-footer" }, [
    "Công cụ tham khảo — không phải khuyến nghị đầu tư. Dữ liệu có độ trễ.",
  ]);
  root.replaceChildren(header, tabHost, footer);

  const builders: Record<Tab, TabBuilder> = {
    "kinh-te": buildKinhTeTab,
    highlight: buildHighlightTab,
    "chinh-tri-xa-hoi": buildChinhTriXaHoiTab,
    "bat-dong-san": buildBatDongSanTab,
    "ban-do-3d": buildBanDo3dTab,
  };
  const cache = new Map<Tab, HTMLElement>();

  function activateTab(tab: Tab): void {
    if (!cache.has(tab)) cache.set(tab, builders[tab]());
    tabHost.replaceChildren(cache.get(tab) as HTMLElement);
    for (const btn of header.querySelectorAll(".tab-btn")) {
      btn.classList.toggle("active", btn.getAttribute("data-tab") === tab);
    }
  }

  async function renderProvince(code: string): Promise<void> {
    // Nav shows no active tab while on a province view.
    for (const btn of header.querySelectorAll(".tab-btn")) btn.classList.remove("active");
    const names = await loadProvinceNames();
    const name = names.get(code) ?? `Tỉnh ${code}`;
    const { ProvinceProfilePage } = await import("../components/province-profile-page.ts");
    const page = new ProvinceProfilePage(code, name);
    tabHost.replaceChildren(page.el);
    void page.load();
  }

  function route(r: Route): void {
    if (r.kind === "province") void renderProvince(r.code);
    else activateTab(r.tab);
  }

  showFirstVisitDisclaimer();
  badge.start();

  // Chat widget: mounted once on body, OUTSIDE tab-host so it persists across
  // tab switches; it reads currentTab() lazily at send time.
  import("../components/chat-widget.ts")
    .then(({ ChatWidget }) => {
      document.body.append(new ChatWidget().el);
    })
    .catch(() => {
      /* chunk load failure: dashboard works without chat */
    });

  // Normalize a legacy deep-link (#chinh-tri/#xa-hoi/#phan-tich) to its new hash
  // once, via replaceState (no history push, no hashchange) BEFORE first route —
  // so Back doesn't bounce and the tab renders once.
  onRouteChange((r) => {
    normalizeLegacyHash();
    route(r);
  });
  normalizeLegacyHash();
  route(currentRoute());
  // Ensure hash reflects the resolved route so refresh/deep-link is stable.
  if (!window.location.hash) goToTab(DEFAULT_TAB);
}

function buildTabNav(): HTMLElement {
  return el(
    "nav",
    { className: "tab-nav" },
    TABS.map((tab) =>
      el(
        "button",
        { className: "tab-btn", "data-tab": tab, onclick: () => goToTab(tab) },
        [TAB_LABELS[tab]],
      ),
    ),
  );
}

// Highlight (Tổng quan) — cross-axis stat tiles + alert feed + the "Tình hình
// Việt Nam" brief, plus the analytics view folded in from the old Phân tích tab:
// national domain ranking + province×week heatmap.
function buildHighlightTab(): HTMLElement {
  // Health-gauge slot: composite economic-health index gauge.
  const healthSlot = el("div", { id: "health-gauge-slot", className: "health-gauge-slot" });
  const healthGauge = new HealthGaugePanel();
  healthSlot.append(healthGauge.el);
  void healthGauge.load();

  const tiles = new StatTilesPanel();
  const pulse = new DomainPulsePanel();
  const alerts = new AlertFeedPanel();
  const brief = new AnalysisBriefPanel({
    id: "brief-vietnam",
    title: "Tình hình Việt Nam",
    topics: ["vietnam"],
  });
  const ranking = new DomainRankingPanel();
  void tiles.load();
  void pulse.load();
  void alerts.load();
  void brief.load();
  void ranking.load();

  // Brief demoted into a collapsed <details> — metrics-first: numbers lead, prose is
  // opt-in. AnalysisBriefPanel renders itself, so wrap its element.
  const briefDetails = el("details", { className: "highlight-brief-details" }, [
    el("summary", {}, ["📄 Bản tin: Tình hình Việt Nam"]),
    brief.el,
  ]);

  const container = el("div", { className: "tab-highlight" }, [
    healthSlot,
    tiles.el,
    pulse.el,
    ranking.el,
    alerts.el,
    briefDetails,
  ]);

  // Heatmap needs province names for its row labels — load them first, then mount.
  void loadProvinceNames().then((names) => {
    const heatmap = new EventsHeatmapPanel(names);
    container.append(heatmap.el);
    void heatmap.load();
  });
  return container;
}

// Chính trị – Xã hội — 3 domain insight blocks (policy / social / hazard). Each
// = daily brief + top events; the Social block adds the weekly sentiment chart
// and the daily sentiment trend. News feeds demoted to a collapsed "Nguồn tin".
function buildChinhTriXaHoiTab(): HTMLElement {
  const intro = el("div", { className: "tab-intro" }, [
    "Chính sách, dân sinh và an toàn — tổng hợp tín hiệu theo lĩnh vực từ sự kiện đã trích xuất.",
  ]);

  // Policy-activity chart leads the policy block (metrics-first).
  const policyChart = new PolicyActivityChart();
  const policyExtra = el("div", { className: "domain-extra" }, [policyChart.el]);
  const policy = new DomainInsightBlock({
    domain: "policy",
    title: "Chính sách & Pháp luật",
    extra: policyExtra,
    briefCollapsed: true,
  });

  // Social block extra: weekly sentiment indicator + daily sentiment trend.
  const sentimentWeekly = new SentimentWeeklyChart("social");
  const sentimentTrend = new SentimentTrendPanel();
  const socialExtra = el("div", { className: "domain-extra" }, [sentimentWeekly.el, sentimentTrend.el]);
  const social = new DomainInsightBlock({
    domain: "social",
    title: "Xã hội & Dân sinh",
    extra: socialExtra,
    briefCollapsed: true,
  });

  const hazard = new DomainInsightBlock({
    domain: "hazard",
    title: "Thiên tai & An toàn",
    briefCollapsed: true,
  });

  void policy.load();
  void policyChart.load();
  void social.load();
  void sentimentWeekly.load();
  void sentimentTrend.load();
  void hazard.load();

  // News feeds demoted into a collapsed <details>. Rendered eagerly inside (the
  // feeds sit behind edge cache; toggle-lazy machinery isn't worth its state).
  const policyNews = new NewsFeedPanel({ id: "news-policy", title: "Tin chính sách", category: "policy" });
  const socialNews = new NewsFeedPanel({ id: "news-social", title: "Tin dân sinh", category: "social" });
  void policyNews.load();
  void socialNews.load();
  const sourceNews = el("details", { className: "source-news" }, [
    el("summary", {}, ["Nguồn tin (chính sách · dân sinh)"]),
    policyNews.el,
    socialNews.el,
  ]);

  return el("div", { className: "tab-ctxh" }, [intro, policy.el, social.el, hazard.el, sourceNews]);
}

// Kinh tế tab — restructured to the domain-insight pattern: economy insight
// block first, then the price/CPI/map surface (behavior unchanged), then the
// news feed demoted into a collapsed "Nguồn tin".
function buildKinhTeTab(): HTMLElement {
  const insight = new DomainInsightBlock({ domain: "economy", title: "Kinh tế — Tổng hợp tín hiệu" });
  void insight.load();

  const goldIntl = createGoldIntlPanel();
  const goldVn = createGoldVnPanel();
  const oil = createOilPanel();
  const fx = createFxPanel();
  const priceRow = el("div", { className: "price-row" }, [goldIntl.el, goldVn.el, oil.el, fx.el]);

  // Derived / market-health charts: gold premium (SJC vs world) + VN-Index with MA20.
  const premium = createGoldPremiumPanel();
  const vnindex = createVnIndexPanel();
  const marketRow = el("div", { className: "price-row" }, [premium.el, vnindex.el]);

  const cpi = new CpiChartPanel();
  const brief = new AnalysisBriefPanel({ topics: ["gold", "oil", "fx", "overview"] });
  const macroRow = el("div", { className: "analysis-row" }, [cpi.el, brief.el]);

  const news = new NewsFeedPanel({
    id: "news-economy",
    title: "Tin kinh tế",
    category: "economy",
    topicFilters: ECONOMY_TOPIC_FILTERS,
    useProvinceFilter: true,
  });
  void news.load();

  // News demoted into <details> (eager render inside). A map province-click sets
  // the global filter — open the details + scroll so the filtered feed is visible
  // (otherwise the flagship map interaction would silently do nothing).
  const sourceNews = el("details", { className: "source-news" }, [
    el("summary", {}, ["Nguồn tin kinh tế"]),
    news.el,
  ]) as HTMLDetailsElement;
  appContext.on("province-change", () => {
    if (appContext.selectedProvince) {
      sourceNews.open = true;
      sourceNews.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  });

  const mapSlot = el("div", { id: "map-slot", className: "map-slot" });
  const container = el("div", { className: "tab-kinh-te" }, [
    insight.el,
    priceRow,
    marketRow,
    macroRow,
    mapSlot,
    sourceNews,
  ]);

  void goldIntl.load();
  void goldVn.load();
  void oil.load();
  void fx.load();
  void premium.load();
  void vnindex.load();
  void cpi.load();
  void brief.load();

  if ("requestIdleCallback" in window) {
    (window as unknown as { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(
      () => void mountMap(mapSlot),
    );
  } else {
    setTimeout(() => void mountMap(mapSlot), 1200);
  }
  return container;
}

// Bất động sản — the map's flagship home: a per-province asking-price choropleth
// (first module with real numeric per-province data), a metric toggle, a click-to
// province price-trend chart, and the real-estate news feed demoted to Nguồn tin.
function buildBatDongSanTab(): HTMLElement {
  const intro = el("div", { className: "tab-intro" }, [
    "Giá rao bất động sản theo tỉnh từ tin đăng Chợ Tốt — trung vị giá/m². ",
    "Chọn tỉnh trên bản đồ để xem xu hướng. Đây là tín hiệu giá RAO, không phải giá giao dịch.",
  ]);
  const label = signalSourceLabel("market");

  const mapSlot = el("div", { className: "map-slot" });
  const container = el("div", { className: "tab-bds" }, [intro, label, mapSlot]);

  // Trend chart + real-estate news feed (demoted into a collapsed details).
  void (async () => {
    const [{ VietnamMap }, { BdsTrendChart }] = await Promise.all([
      import("../components/map-shell.ts"),
      import("../components/bds-trend-chart.ts"),
    ]);
    const trend = new BdsTrendChart("bds-canho-ban-m2");
    const map = new VietnamMap({
      mode: "bds:bds-canho-ban-m2",
      showToggle: true,
      onProvinceClick: (code) => {
        if (code) void trend.loadProvince(code);
      },
    });
    // The tab is cached (built once), so the map's WebGL context persists across
    // tab switches — same as the Kinh tế density map. One idle context is cheaper
    // than a destroy/remount lifecycle and avoids a dead canvas on re-entry.
    await map.mount(mapSlot);

    const news = new NewsFeedPanel({
      id: "news-bds",
      title: "Tin bất động sản",
      category: "economy",
      initialTopic: "realestate",
    });
    void news.load();
    const sourceNews = el("details", { className: "source-news" }, [
      el("summary", {}, ["Nguồn tin bất động sản"]),
      news.el,
    ]);
    container.append(trend.el, sourceNews);
  })();

  return container;
}

function buildBanDo3dTab(): HTMLElement {
  const intro = el("div", { className: "tab-intro" }, [
    "Du lịch Việt Nam 3D — 34 tỉnh, thành như một công viên thu nhỏ: địa danh biểu tượng, ",
    "ngày và đêm luân chuyển, thuyền – tàu – máy bay qua lại, pháo hoa đêm lễ hội. ",
    "Rê chuột để khám phá, nhấp một tỉnh để xem điểm đến, mùa đẹp và đặc sản.",
  ]);
  const mapSlot = el("div", { className: "map-slot" });
  const container = el("div", { className: "tab-ban-do-3d" }, [intro, mapSlot]);

  // Lazy-import the Three.js panel so the engine only loads on this tab. The tab is
  // cached (built once), so the WebGL context persists across tab switches.
  void (async () => {
    const { Map3D } = await import("../components/map-3d-panel.ts");
    const map = new Map3D();
    await map.mount(mapSlot);
  })();

  return container;
}

async function mountMap(slot: HTMLElement): Promise<void> {
  try {
    const { mountVietnamMap } = await import("../components/map-shell.ts");
    await mountVietnamMap(slot);
  } catch {
    slot.remove();
  }
}
